// =====================================================================
// useGlobalSearch — open/close state for the navbar's command-palette
// search (GlobalSearch.jsx), plus the app-wide Cmd/Ctrl+K shortcut.
//
// The match/index logic itself is framework-free (utils/globalSearchLogic.mjs,
// covered by test:logic) — this hook is only the thin React/DOM wiring:
// state + the global keydown listener. Lives in Header (always mounted
// inside Layout for every authenticated page) so the shortcut works from
// anywhere in the app shell, not just while the search input itself is
// focused.
//
// This file also exports useGlobalSearchEntities() (below), a second,
// independent hook that owns the entity-search data source (districts come
// free from data/rwanda.js; models/alerts/tables are fetched). It's driven
// by the `open` prop GlobalSearch.jsx already receives from Header, rather
// than by this file's own isOpen — Header only forwards `open`/`onClose` to
// GlobalSearch, so entity-fetch wiring has to live where `open` is already
// available (GlobalSearch itself) without a second, disconnected instance
// of THIS hook's isOpen ever existing.
// =====================================================================

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { hasAnyPermission } from '../utils/permissions.js'
import { ENTITY_REQUIRE_ANY } from '../utils/globalSearchLogic.mjs'
import { notebooksAPI, alertsAPI, schemaAPI } from '../services/api'

export default function useGlobalSearch() {
  const [isOpen, setIsOpen] = useState(false)

  const openSearch = useCallback(() => setIsOpen(true), [])
  const closeSearch = useCallback(() => setIsOpen(false), [])

  useEffect(() => {
    const onKeyDown = (event) => {
      const isK = event.key === 'k' || event.key === 'K'
      if (!isK || !(event.metaKey || event.ctrlKey)) return
      // Prevent the browser/OS's own Cmd/Ctrl+K (e.g. Chrome's address-bar
      // search) from firing alongside this — it's the same shortcut on
      // purpose (a command-palette convention), so the app's own dialog
      // should be the only thing that responds.
      event.preventDefault()
      setIsOpen((prev) => !prev)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return { isOpen, openSearch, closeSearch }
}

// A cached entity fetch is reused across re-opens for this long before the
// next open re-fetches it — re-opening the palette a minute later shouldn't
// show an alert that's since been acknowledged forever, but re-opening it a
// second later (the common case) should be instant, not a spinner.
const ENTITY_STALE_MS = 60_000

// Debounces the OPEN-triggered fetch kickoff itself (not per-keystroke —
// once fetched, matching against every keystroke is pure client-side
// filtering in globalSearchLogic.mjs, zero network calls). This just
// absorbs a rapid open/close/open flicker (e.g. a stuck Cmd+K key) into a
// single fetch instead of one per flicker.
const FETCH_DEBOUNCE_MS = 150

const emptyEntityState = () => ({ data: [], loading: false, error: false, fetchedAt: 0 })

// One (fetch fn, unwrap fn) -> { state, ensureLoaded } source, shared by
// every entity type below. `ensureLoaded` is a no-op if a fetch is already
// in flight or the cached data is still fresh (< ENTITY_STALE_MS old).
function useEntitySource(fetchFn, unwrap) {
  const [state, setState] = useState(emptyEntityState)
  const fetchedAtRef = useRef(0)
  const inFlightRef = useRef(false)

  const ensureLoaded = useCallback(() => {
    if (inFlightRef.current) return
    if (fetchedAtRef.current && Date.now() - fetchedAtRef.current < ENTITY_STALE_MS) return

    inFlightRef.current = true
    setState((s) => ({ ...s, loading: true, error: false }))
    fetchFn()
      .then((res) => {
        fetchedAtRef.current = Date.now()
        setState({ data: unwrap(res), loading: false, error: false, fetchedAt: fetchedAtRef.current })
      })
      .catch(() => {
        // Best-effort search source: a failed entity fetch degrades to
        // "that type just doesn't appear in results" (see GlobalSearch.jsx's
        // loading/error hint), never a blocking error for the whole palette
        // — pages still search fine with zero network calls.
        setState((s) => ({ ...s, loading: false, error: true }))
      })
      .finally(() => {
        inFlightRef.current = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return [state, ensureLoaded]
}

// Entity data source for GlobalSearch.jsx: models/notebooks + active alerts
// + datasets/tables, fetched (at most once per ENTITY_STALE_MS window) each
// time the palette opens. Districts/sectors aren't here — they're static
// (data/rwanda.js), so globalSearchLogic.mjs's districtEntities() needs no
// fetch at all; GlobalSearch.jsx builds those directly.
//
// `open` is the same boolean GlobalSearch.jsx receives from Header as a
// prop — passing it in here (rather than this file's own isOpen) is what
// lets this hook react to the real open/close the user sees without a
// second useGlobalSearch() call spinning up its own, disconnected isOpen
// and a duplicate keydown listener.
export function useGlobalSearchEntities(open) {
  const { user } = useAuth()
  const [notebooks, ensureNotebooksLoaded] = useEntitySource(
    () => notebooksAPI.list(),
    (res) => (Array.isArray(res?.notebooks) ? res.notebooks : [])
  )
  const [alerts, ensureAlertsLoaded] = useEntitySource(
    () => alertsAPI.list({ status: 'active', limit: 100 }),
    (res) => (Array.isArray(res?.alerts) ? res.alerts : [])
  )
  const [tables, ensureTablesLoaded] = useEntitySource(
    () => schemaAPI.getTables({ limit: 100 }),
    (res) => (Array.isArray(res?.tables) ? res.tables : [])
  )

  // Alerts alone are gated server-side on view_predictions (see
  // globalSearchLogic.mjs's ENTITY_REQUIRE_ANY + the alerts route). A user
  // without it would just get a silent 403 + the interceptor's "Access
  // denied" toast every time they opened ⌘K, for a search they never typed
  // — so this hook simply never calls the endpoint for them, same
  // permission source globalSearchLogic.mjs filters the results with.
  const canSeeAlerts = hasAnyPermission(user, ENTITY_REQUIRE_ANY.alert)

  const debounceRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    debounceRef.current = setTimeout(() => {
      ensureNotebooksLoaded()
      ensureTablesLoaded()
      if (canSeeAlerts) ensureAlertsLoaded()
    }, FETCH_DEBOUNCE_MS)
    return () => clearTimeout(debounceRef.current)
  }, [open, canSeeAlerts, ensureNotebooksLoaded, ensureTablesLoaded, ensureAlertsLoaded])

  return { notebooks, alerts, tables }
}
